export interface ScoreBreakdown {
  label: string;
  points: number;
}

export interface ScoreResult {
  points: number;
  breakdown: ScoreBreakdown[];
}

export interface ScoringEvent {
  clustersPopped: number;
  bubblesDropped: number;
  bankShots: number;
  chainDepth: number;
  nearFailure: boolean;
  isSetupShot: boolean;
  frenzyActive?: boolean;
  governorLocked?: boolean;
  /** Bank-shot coefficient in X10 format (e.g. 15 = 1.5x). Defaults to 15. */
  bankCoefX10?: number;
}

/**
 * Calculate score for a scoring event. All arithmetic is integer-only.
 *
 * @param event - The scoring event details
 * @param multiplierX10 - Current multiplier in X10 format (e.g. 15 = 1.5x)
 * @returns ScoreResult with total points and breakdown
 */
export function calculateScore(
  event: ScoringEvent,
  multiplierX10: number,
): ScoreResult {
  const breakdown: ScoreBreakdown[] = [];

  // Base points: cluster pops + dropped bubbles
  const clusterPoints = event.clustersPopped * 10;
  const dropPoints = event.bubblesDropped * 15;
  let basePoints = clusterPoints + dropPoints;

  if (clusterPoints > 0) {
    breakdown.push({ label: "Clusters popped", points: clusterPoints });
  }
  if (dropPoints > 0) {
    breakdown.push({ label: "Bubbles dropped", points: dropPoints });
  }

  // Bank shot bonus: bankCoefX10/10 per bounce (integer math; default 1.5x)
  if (event.bankShots > 0) {
    const bankCoefX10 = event.bankCoefX10 ?? 15;
    const bankBonus = Math.trunc(basePoints * bankCoefX10 / 10) * event.bankShots;
    basePoints += bankBonus;
    breakdown.push({ label: "Bank shot bonus", points: bankBonus });
  }

  // Chain bonus: quadratic
  if (event.chainDepth > 0) {
    const chainBonus = event.chainDepth * event.chainDepth * 25;
    basePoints += chainBonus;
    breakdown.push({ label: "Chain bonus", points: chainBonus });
  }

  // Frenzy: double base before multiplier
  if (event.frenzyActive) {
    basePoints = basePoints * 2;
    breakdown.push({ label: "Frenzy (2x)", points: basePoints / 2 });
  }

  // Apply multiplier (integer division via Math.trunc)
  const totalPoints = Math.trunc(basePoints * multiplierX10 / 10);

  if (multiplierX10 !== 10) {
    breakdown.push({
      label: `Multiplier (${multiplierX10 / 10}x)`,
      points: totalPoints - basePoints,
    });
  }

  return { points: totalPoints, breakdown };
}
